import { Program } from '@citruscamps/citrus-client'
import { faLongArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime, Interval } from 'luxon'
import React, { useEffect, useState } from 'react'
import translations from '../../../translations/en.json'
import { Calendar } from '../../Calendar/Calendar'
import { DateInput } from '../../DateInput/DateInput'

interface IProps {
  values: Interval
  program?: Program
  onSelect: (value: Interval) => any
}

export const DateMacroRange = ({ values, program, onSelect: handleSelectRange }: IProps) => {
  const [{ start: startDate, end: endDate }, setInterval] = useState<Partial<Interval>>({
    start: values.start,
    end: values.end,
  })

  useEffect(() => {
    const initialStartDate = values.start?.toISODate()
    const initialEndDate = values.end?.toISODate()
    setInterval({
      start: DateTime.fromFormat(initialStartDate || '', 'yyyy-MM-dd'),
      end: DateTime.fromFormat(initialEndDate || '', 'yyyy-MM-dd'),
    })
  }, [values.start, values.end])

  const isInside = (dt: DateTime): boolean =>
    !!startDate && !!endDate && Interval.fromDateTimes(startDate, endDate).contains(dt)
  const isFrom = (dt: DateTime): boolean => !!startDate && dt.hasSame(startDate, 'day')
  const isTo = (dt: DateTime): boolean => !!endDate && dt.hasSame(endDate, 'day')
  const isFuture = (dt: DateTime): boolean =>
    dt && dt.startOf('day') > DateTime.local().startOf('day')
  const programStartDate: DateTime = program
    ? DateTime.fromJSDate(new Date(program.created), {
        zone: program.timezone,
      }).startOf('year')
    : DateTime.fromObject({ year: 2020 }).startOf('year')
  const isRelevant = (dt: DateTime): boolean => dt > programStartDate

  const isToday = (dt: DateTime): boolean => dt.hasSame(DateTime.local(), 'day')

  const isStartValid: boolean = !startDate || (!isFuture(startDate) && isRelevant(startDate))
  const isEndValid: boolean = !endDate || (!isFuture(endDate) && isRelevant(endDate))
  const startErrorMessage =
    startDate && isFuture(startDate)
      ? (translations as any)['error.date_macro_start_future']
      : startDate && !isRelevant(startDate)
      ? (translations as any)['error.date_macro_start_relevant']
      : undefined
  const endErrorMessage =
    endDate && isFuture(endDate)
      ? (translations as any)['error.date_macro_end_future']
      : endDate && !isRelevant(endDate)
      ? (translations as any)['error.date_macro_end_relevant']
      : undefined

  return (
    <>
      <div className="input-group p-2">
        <DateInput
          value={startDate || undefined}
          className={'form-control form-control-sm' + (!isStartValid ? ' is-invalid' : '')}
          disabledPopover={true}
          onChangeValue={(start?: DateTime, evt?: React.ChangeEvent<HTMLInputElement>) => {
            const isValid = evt?.target?.checkValidity()
            let end: DateTime | undefined = endDate || undefined
            if (start && end && start > end && isValid) {
              end = undefined
            }
            setInterval({ start, end })
            if (start && end && Interval.fromDateTimes(start, end).isValid && !!isValid) {
              handleSelectRange(Interval.fromDateTimes(start, end))
            }
          }}
          onClick={(evt) => evt.preventDefault()}
          min={programStartDate.toISODate() || undefined}
          max={endDate?.toISODate() || DateTime.local().toISODate() || undefined}
        />
        <span className="input-group-text">
          <FontAwesomeIcon icon={faLongArrowRight} />
        </span>
        <DateInput
          value={endDate || undefined}
          className={'form-control form-control-sm' + (!isEndValid ? ' is-invalid' : '')}
          disabledPopover={true}
          onChangeValue={(d?: DateTime, evt?: React.ChangeEvent<HTMLInputElement>) => {
            const isValid = evt?.target?.checkValidity()
            setInterval((i) => ({ start: i.start, end: d }))
            const i: Partial<Interval> = { start: startDate, end: d }
            if (i.start && i.end && Interval.fromDateTimes(i.start, i.end).isValid && !!isValid) {
              handleSelectRange(Interval.fromDateTimes(i.start, i.end))
            }
          }}
          onClick={(evt) => evt.preventDefault()}
          min={startDate?.toISODate() || programStartDate.toISODate() || undefined}
          max={DateTime.local().toISODate() || undefined}
        />
      </div>
      <div className="invalid-feedback d-block">
        {startErrorMessage || endErrorMessage || '\u00A0'}
      </div>
      <div>
        <Calendar
          value={startDate && endDate ? [startDate, endDate] : startDate ? [startDate] : undefined}
          className="border-0"
          isFollowing={false}
          onSelect={(value: DateTime) => {
            let start: DateTime | undefined = startDate || undefined
            let end: DateTime | undefined = endDate || undefined
            const beforeStart: boolean = !start || value < start
            const isAvailable: boolean = !isFuture(value) && isRelevant(value)
            const isAbleToStart: boolean = (beforeStart || !!end) && isAvailable
            const isAbleToEnd: boolean = !!start && !end && !beforeStart && isAvailable && !!start
            if (isAbleToStart) {
              // set start date
              end = undefined
              start = value.startOf('day')
              setInterval({ start, end })
            } else if (isAbleToEnd) {
              // set end date
              end = value.endOf('day')
              const i: Interval = Interval.fromDateTimes(start as DateTime, end)
              setInterval({ start, end })
              if (start && end && i.isValid) {
                handleSelectRange(i)
              }
            } else {
              // invalid
              setInterval({ start, end })
            }
          }}
          DayCell={({
            className,
            value,
          }: {
            className?: string
            value: DateTime
          }): React.ReactElement => {
            const classNameList: string[] = [className || '', 'custom-day']
            if (isFrom(value) || isTo(value)) classNameList.push('range')
            if (isInside(value)) classNameList.push('faded')
            if (isFuture(value) && !isToday(value)) classNameList.push('btn-light')
            if (
              !isRelevant(value) &&
              isFuture(value) &&
              !isToday(value) &&
              !(isFrom(value) || isInside(value) || isTo(value))
            ) {
              classNameList.push('text-body-secondary')
            }
            if (isFuture(value) && !isToday(value)) classNameList.push('outside')
            return <div className={classNameList.join(' ').trim()}>{value.day}</div>
          }}
        />
      </div>
    </>
  )
}
